import { useState } from "react";
import { Zap, X, ShieldAlert, ArrowRight, Apple, Smartphone, ExternalLink } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";

/**
 * AppStoreTriageBubble — heavily-themed comic-style speech bubble that pops
 * out of a BattleCard when an Apple- or Google-billed subscription is detected.
 *
 * Props:
 *   - vendor: "apple" | "google"
 *   - platform: "ios" | "android" | "desktop"  (from useDevicePlatform)
 *   - subName: string  (used in the triage instructions)
 *   - onDismiss(): hide this bubble (parent handles persistence)
 */
const STORE_LABEL = {
  apple: "App Store",
  google: "Play Store",
};

const STORE_BRAND = {
  apple:  { Icon: Apple,      color: "from-slate-900 to-slate-700", chip: "bg-slate-900 text-white" },
  google: { Icon: Smartphone, color: "from-emerald-500 to-emerald-700", chip: "bg-emerald-500 text-white" },
};

export default function AppStoreTriageBubble({ vendor, platform, subName, onDismiss }) {
  const [open, setOpen] = useState(false);
  const storeLabel = STORE_LABEL[vendor] || "App Store";
  const brand = STORE_BRAND[vendor] || STORE_BRAND.apple;

  return (
    <>
      <div
        className="relative mb-4 mmh-comic-pop"
        data-testid="app-store-triage-bubble"
        data-vendor={vendor}
        data-platform={platform}
      >
        {/* Speech bubble */}
        <div className="relative rounded-2xl border-[3px] border-slate-900 bg-gradient-to-br from-yellow-300 via-amber-300 to-yellow-400 px-3 pt-3 pb-3.5 mmh-comic-shadow">
          {/* "POW!" exclamation chip */}
          <div className="absolute -top-3 -left-3 inline-flex items-center gap-1 px-2 py-0.5 rounded-full border-[3px] border-slate-900 bg-red-500 text-white text-[10px] font-black uppercase tracking-[0.18em] shadow-[2px_2px_0_#0F172A] mmh-comic-wiggle">
            <Zap className="w-3 h-3" strokeWidth={3} />
            POW!
          </div>

          {/* Dismiss X */}
          <button
            type="button"
            onClick={onDismiss}
            className="absolute -top-2 -right-2 w-7 h-7 rounded-full border-[3px] border-slate-900 bg-white text-slate-900 hover:bg-slate-100 flex items-center justify-center transition-all shadow-[2px_2px_0_#0F172A]"
            data-testid="triage-bubble-dismiss"
            aria-label="Dismiss App Store Triage"
          >
            <X className="w-3 h-3" strokeWidth={3.5} />
          </button>

          <div className="flex items-start gap-2.5 pl-1">
            <div className={`w-9 h-9 rounded-xl border-[3px] border-slate-900 ${brand.chip} flex items-center justify-center flex-shrink-0 shadow-[2px_2px_0_#0F172A]`}>
              <brand.Icon className="w-4 h-4" strokeWidth={2.8} />
            </div>
            <div className="flex-1 min-w-0">
              <p
                className="text-sm font-black text-slate-900 leading-tight uppercase tracking-tight"
                style={{ fontFamily: "Outfit, sans-serif", letterSpacing: "-0.01em" }}
              >
                <ShieldAlert className="inline-block w-4 h-4 mr-1 -mt-0.5" strokeWidth={3} />
                Hero Shield has found a villain!
              </p>
              <p
                className="mt-1 text-[12.5px] font-extrabold text-slate-900 leading-snug"
                style={{ fontFamily: "Outfit, sans-serif" }}
              >
                Stop forgotten <span className="underline decoration-2 decoration-red-500 underline-offset-2">{storeLabel}</span> subscriptions. Time to determine its fate.
              </p>
            </div>
          </div>

          <div className="mt-3 flex items-center gap-2">
            <Button
              type="button"
              size="sm"
              onClick={() => setOpen(true)}
              className="h-9 flex-1 rounded-xl border-[3px] border-slate-900 bg-slate-900 hover:bg-slate-800 text-amber-300 font-black uppercase tracking-wider gap-1.5 shadow-[2px_2px_0_#0F172A] text-xs"
              data-testid="triage-bubble-action"
            >
              <ShieldAlert className="w-3.5 h-3.5" strokeWidth={3} />
              Determine its fate
              <ArrowRight className="w-3.5 h-3.5" strokeWidth={3} />
            </Button>
          </div>

          {/* Speech bubble tail (pointing down toward the card) */}
          <div
            className="absolute -bottom-[14px] left-9 w-4 h-4 rotate-45 bg-amber-300 border-r-[3px] border-b-[3px] border-slate-900"
            aria-hidden="true"
          />
        </div>
      </div>

      {/* Triage instructions dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md rounded-3xl border-[3px] border-slate-900 p-0 overflow-hidden mmh-comic-shadow" data-testid="triage-dialog">
          <div className={`bg-gradient-to-br ${brand.color} text-white px-6 pt-6 pb-5 relative overflow-hidden`}>
            <div className="absolute -top-8 -right-8 w-32 h-32 bg-amber-400/30 rounded-full blur-2xl" aria-hidden="true" />
            <div className="relative flex items-center gap-3">
              <div className="w-12 h-12 rounded-2xl border-[3px] border-slate-900 bg-amber-300 text-slate-900 flex items-center justify-center shadow-[3px_3px_0_#0F172A]">
                <brand.Icon className="w-6 h-6" strokeWidth={2.8} />
              </div>
              <div className="min-w-0">
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-400/20 text-amber-200 text-[10px] font-black uppercase tracking-[0.2em]">
                  <Zap className="w-3 h-3" strokeWidth={3} /> Triage
                </span>
                <DialogTitle
                  className="text-2xl font-black tracking-tight text-white mt-1 uppercase"
                  style={{ fontFamily: "Outfit, sans-serif" }}
                >
                  Cancel via {storeLabel}
                </DialogTitle>
                <DialogDescription className="text-slate-200 text-xs font-bold mt-0.5 truncate">
                  {subName || "This subscription"} is billed by {vendor === "apple" ? "Apple" : "Google"}.
                </DialogDescription>
              </div>
            </div>
          </div>

          <div className="px-6 py-5 bg-white">
            <p className="text-[11px] font-black uppercase tracking-[0.2em] text-slate-400">
              Why this is special
            </p>
            <p className="mt-1.5 text-[13px] leading-snug text-slate-700 font-semibold">
              Subscriptions billed through {storeLabel} can ONLY be canceled inside {vendor === "apple" ? "Apple's settings" : "the Play Store app"} — the merchant can't cancel them for you.
            </p>

            <p className="mt-5 text-[11px] font-black uppercase tracking-[0.2em] text-slate-400">
              How to cancel
            </p>
            <ol className="mt-2 space-y-2.5">
              {(vendor === "apple"
                ? [
                    platform === "ios" ? "Open Settings → tap your name at the top" : "On an iPhone or iPad: open Settings → tap your name",
                    "Tap Subscriptions",
                    `Find ${subName || "this subscription"} → tap Cancel Subscription`,
                    "Confirm — your access stays until the end of the period",
                  ]
                : [
                    platform === "android" ? "Open the Play Store app on this device" : "On an Android device: open the Play Store app",
                    "Tap your profile icon (top right) → Payments & subscriptions → Subscriptions",
                    `Find ${subName || "this subscription"} → tap Cancel subscription`,
                    "Confirm — billing stops at the next renewal date",
                  ]
              ).map((step, i) => (
                <li key={step} className="flex items-start gap-2.5">
                  <span className={`flex-shrink-0 w-6 h-6 rounded-full border-[2px] border-slate-900 ${brand.chip} text-[10px] font-black flex items-center justify-center shadow-[1.5px_1.5px_0_#0F172A]`}>
                    {i + 1}
                  </span>
                  <span className="text-[13px] leading-snug text-slate-800 font-semibold">{step}</span>
                </li>
              ))}
            </ol>

            <p className="mt-5 text-[11px] text-slate-500 leading-relaxed font-medium">
              Once canceled in {storeLabel}, hit <span className="font-black text-slate-900">Defeat Bill</span> on this card to lock in the win.
            </p>
          </div>

          <DialogFooter className="px-6 pb-6 pt-0 flex-col sm:flex-col gap-2">
            {vendor === "apple" && platform === "ios" && (
              <a
                href="https://apps.apple.com/account/subscriptions"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 w-full h-11 rounded-xl border-[3px] border-slate-900 bg-amber-300 hover:bg-amber-400 text-slate-900 font-black uppercase tracking-wider text-sm shadow-[2px_2px_0_#0F172A]"
                data-testid="triage-deeplink"
              >
                <ExternalLink className="w-4 h-4" strokeWidth={3} />
                Open subscriptions
              </a>
            )}
            {vendor === "google" && platform === "android" && (
              <a
                href="https://play.google.com/store/account/subscriptions"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 w-full h-11 rounded-xl border-[3px] border-slate-900 bg-amber-300 hover:bg-amber-400 text-slate-900 font-black uppercase tracking-wider text-sm shadow-[2px_2px_0_#0F172A]"
                data-testid="triage-deeplink"
              >
                <ExternalLink className="w-4 h-4" strokeWidth={3} />
                Open Play subscriptions
              </a>
            )}
            <Button
              type="button"
              variant="ghost"
              onClick={() => setOpen(false)}
              className="w-full h-10 rounded-xl text-slate-600 hover:text-slate-900 hover:bg-slate-100 font-bold"
              data-testid="triage-dialog-close"
            >
              Got it, hero
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
